import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, BookOpen, BellRing, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken } from "../lib/api";
import { CATEGORIES, PRIORITIES, TOPIC_STATUS, TOPIC_TONE, ACTION_HI, fmtIST } from "../lib/research";
import { isAdmin } from "../lib/customers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DailyList } from "../components/research/DailyList";
import { TemplatesEditor } from "../components/research/TemplatesEditor";

const CARDS = [["total_topics", "कुल रिसर्च"], ["open_topics", "अधूरी रिसर्च"], ["today_due", "आज के बाकी चेक", "orange"], ["missed", "छूटे हुए चेक", "red"], ["done_today", "आज पूरे किए चेक", "green"]];
const CARD_TONE = { orange: "border-orange-300 bg-orange-50", red: "border-red-300 bg-red-50", green: "border-green-300 bg-green-50" };
const TAB_STATUS = { all: "", pending: "not_started", running: "in_progress,on_hold", done: "done" };

const NewTopic = ({ onClose }) => {
  const navigate = useNavigate();
  const [f, setF] = useState({ title: "", category: "form", priority: "normal", template_id: "" });
  const [tpls, setTpls] = useState([]);
  useEffect(() => { api.get("/research/templates").then((r) => setTpls(r.data)).catch(() => {}); }, []);
  const save = async () => {
    if (!f.title.trim()) { toast.error("विषय का नाम लिखें"); return; }
    try { const r = await api.post("/research/topics", { ...f, template_id: f.template_id || null }); navigate(`/admin/research/${r.data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "सेव नहीं हुआ"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-topic-dialog">
        <DialogHeader><DialogTitle>नई रिसर्च</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">विषय का नाम *</Label><Input autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} data-testid="new-topic-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">कैटेगरी</Label><select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full h-9 border rounded-md px-2" data-testid="new-topic-category">{Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><Label className="text-xs">प्राथमिकता</Label><select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="w-full h-9 border rounded-md px-2" data-testid="new-topic-priority">{Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          </div>
          <div><Label className="text-xs">चेकलिस्ट टेम्पलेट</Label><select value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })} className="w-full h-9 border rounded-md px-2" data-testid="new-topic-template">
            <option value="">खाली चेकलिस्ट</option>{tpls.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.questions.length} सवाल)</option>)}</select></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>रद्द करें</Button><Button onClick={save} className="bg-teal-700 text-white" data-testid="new-topic-save">सेव करें</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TopicList = ({ items, onOpen }) => (
  <div className="space-y-2 mt-3">
    {items.length === 0 && <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">कोई रिसर्च नहीं।</div>}
    {items.map((t) => (
      <button key={t.id} onClick={() => onOpen(t)} className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm transition-shadow" data-testid={`topic-row-${t.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-semibold text-slate-900">{t.title} {t.priority === "urgent" && <span className="ml-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px]">जरूरी</span>}</div>
          <span className={`px-2.5 py-0.5 rounded-full text-xs ${TOPIC_TONE[t.status]}`}>{TOPIC_STATUS[t.status]}</span>
        </div>
        <div className="text-xs text-slate-500 mt-1">{CATEGORIES[t.category]} · कुल {t.progress.total} सवाल • {t.progress.done} पूरे • {t.progress.total - t.progress.done} बाकी · अपडेट {fmtIST(t.updated_at)}
          {t.last_date && ` · अंतिम तारीख ${t.last_date}`}{t.next_date && ` · अगली रिसर्च ${t.next_date}`}</div>
      </button>
    ))}
  </div>
);

const HistoryTab = ({ reloadAll }) => {
  const [h, setH] = useState([]);
  const [trash, setTrash] = useState({ topics: [], sources: [] });
  const load = () => {
    api.get("/research/history").then((r) => setH(r.data)).catch(() => {});
    Promise.all([api.get("/research/topics", { params: { trash: true } }), api.get("/research/sources", { params: { trash: true } })])
      .then(([a, b]) => setTrash({ topics: a.data, sources: b.data })).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const act = (p) => p.then(() => { load(); reloadAll(); }).catch((e) => toast.error(e?.response?.data?.detail || "असफल"));
  const all = [...trash.topics.map((t) => ({ ...t, kind: "topics", name: t.title })), ...trash.sources.map((s) => ({ ...s, kind: "sources" }))];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-3">
      <div className="lg:col-span-2 rounded-xl border bg-white p-4" data-testid="history-list">
        <div className="font-semibold mb-2">हिस्ट्री</div>
        {h.map((x) => <div key={x.id} className="text-sm border-b border-slate-100 py-1.5"><b>{x.title || "—"}</b> · {ACTION_HI[x.action] || x.action} · {x.detail}<div className="text-xs text-slate-500">{fmtIST(x.at)}</div></div>)}
      </div>
      <div className="rounded-xl border bg-white p-4" data-testid="trash-list">
        <div className="font-semibold mb-2 flex items-center gap-1"><Trash2 className="w-4 h-4" /> Trash</div>
        {all.length === 0 && <div className="text-sm text-slate-500">खाली</div>}
        {all.map((x) => (
          <div key={x.id} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b" data-testid={`trash-${x.id}`}>
            <span className="truncate">{x.kind === "topics" ? "रिसर्च" : "स्रोत"}: {x.name}</span>
            <span className="flex gap-1 shrink-0">
              <Button size="sm" variant="outline" onClick={() => act(api.post(`/research/${x.kind}/${x.id}/restore`))} data-testid={`trash-restore-${x.id}`}><RotateCcw className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => window.confirm("हमेशा के लिए हटाएँ?") && act(api.delete(`/research/${x.kind}/${x.id}`, { params: { permanent: true } }))} data-testid={`trash-purge-${x.id}`}><Trash2 className="w-3 h-3" /></Button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Reminders = ({ s, reload }) => {
  const [time, setTime] = useState("");
  const saveTimes = (list) => api.put("/research/settings", { reminder_times: list }).then(reload);
  const perm = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm space-y-2" data-testid="reminders-panel">
      {(s.deadlines || []).map((d) => <div key={d.id} className="text-red-700" data-testid={`deadline-${d.id}`}>⚠ "{d.title}" की अंतिम तारीख {d.days_left === 0 ? "आज" : `${d.days_left} दिन में`} ({d.last_date})</div>)}
      {(s.next_actions || []).map((d) => <div key={d.id} className="text-amber-700">→ "{d.title}": {d.next_action || "अगली रिसर्च"} ({d.next_date})</div>)}
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <BellRing className="w-4 h-4" /> चेक बाकी रहने पर दोबारा याद दिलाने का समय:
        {(s.reminder_times || []).map((t) => <button key={t} onClick={() => saveTimes(s.reminder_times.filter((x) => x !== t))} className="px-2 py-0.5 rounded-full bg-slate-100">{t} ✕</button>)}
        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-7 w-28" data-testid="reminder-time" />
        <Button size="sm" variant="outline" className="h-7" onClick={() => { if (time) saveTimes([...(s.reminder_times || []), time]); setTime(""); }} data-testid="reminder-time-add">जोड़ें</Button>
        {perm === "default" && <Button size="sm" variant="outline" className="h-7" onClick={() => Notification.requestPermission().then(reload)} data-testid="enable-browser-notif">ब्राउज़र सूचना चालू करें</Button>}
        {perm === "granted" && <span className="text-green-700">ब्राउज़र सूचना चालू (केवल वेबसाइट खुली होने पर)</span>}
        {perm === "denied" && <span className="text-red-600">ब्राउज़र सूचना बंद है — ब्राउज़र सेटिंग से चालू करें। डैशबोर्ड रिमाइंडर फिर भी काम करेंगे।</span>}
      </div>
    </div>
  );
};

export default function ResearchPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [summary, setSummary] = useState({});
  const [topics, setTopics] = useState([]);
  const [sources, setSources] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [newTopic, setNewTopic] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);

  const loadSummary = () => api.get("/research/summary").then((r) => setSummary(r.data)).catch((e) => { if ([401, 403].includes(e?.response?.status)) navigate("/admin/login"); });
  const loadSources = () => api.get("/research/sources").then((r) => setSources(r.data)).catch(() => {});
  const loadTopics = () => api.get("/research/topics", { params: { ...(q.trim() && { q }), ...(cat && { category: cat }), ...(TAB_STATUS[tab] && { status: TAB_STATUS[tab] }) } }).then((r) => setTopics(r.data)).catch(() => toast.error("खोज असफल — फिर कोशिश करें"));
  const reloadAll = () => { loadSummary(); loadSources(); loadTopics(); };

  useEffect(() => {
    if (!loadAuthToken() || !isAdmin()) { navigate("/admin/login"); return; }
    loadSummary(); loadSources();
    const iv = setInterval(() => { loadSummary(); loadSources(); }, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);
  useEffect(() => { const id = setTimeout(loadTopics, 300); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q, cat, tab]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8" data-testid="research-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-4" data-testid="research-back"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 flex items-center gap-2"><BookOpen className="w-6 h-6 text-teal-700" /> रिसर्च और डेली चेक</h1>
          <p className="text-sm text-slate-500 mt-1">केवल Admin के लिए · सारा डेटा सुरक्षित सर्वर पर सेव होता है</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setTplOpen(true)} data-testid="templates-btn">टेम्पलेट</Button>
          <Button variant="outline" onClick={() => setTab("daily")} data-testid="goto-daily-btn">नया दैनिक स्रोत जोड़ें</Button>
          <Button onClick={() => setNewTopic(true)} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="add-topic-btn"><Plus className="w-4 h-4 mr-1" /> नई रिसर्च जोड़ें</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
        {CARDS.map(([k, l, tone]) => (
          <div key={k} className={`rounded-xl border p-3 ${summary[k] && tone ? CARD_TONE[tone] : "border-slate-200 bg-white"}`} data-testid={`rstat-${k}`}>
            <div className="text-xs text-slate-500">{l}</div><div className="text-2xl font-semibold mt-1">{summary[k] ?? "—"}</div>
          </div>
        ))}
      </div>
      <Reminders s={summary} reload={loadSummary} />
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="flex w-full overflow-x-auto justify-start sm:grid sm:grid-cols-6">
          <TabsTrigger value="all" data-testid="rtab-all">सभी रिसर्च</TabsTrigger>
          <TabsTrigger value="pending" data-testid="rtab-pending">रिसर्च बाकी</TabsTrigger>
          <TabsTrigger value="running" data-testid="rtab-running">रिसर्च चल रही है</TabsTrigger>
          <TabsTrigger value="done" data-testid="rtab-done">पूरी हुई रिसर्च</TabsTrigger>
          <TabsTrigger value="daily" data-testid="rtab-daily">रोज चेक करने वाली सूची</TabsTrigger>
          <TabsTrigger value="history" data-testid="rtab-history">हिस्ट्री</TabsTrigger>
        </TabsList>
        {["all", "pending", "running", "done"].includes(tab) && (
          <div className="flex flex-wrap gap-2 mt-4">
            <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="विषय, सवाल, जवाब या नोट्स में खोजें…" className="pl-9" data-testid="research-search" /></div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 border rounded-md px-2 text-sm bg-white" data-testid="research-category-filter">
              <option value="">सभी कैटेगरी</option>{Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </div>
        )}
        {["all", "pending", "running", "done"].map((k) => <TabsContent key={k} value={k}><TopicList items={topics} onOpen={(t) => navigate(`/admin/research/${t.id}`)} /></TabsContent>)}
        <TabsContent value="daily"><DailyList sources={sources} reload={() => { loadSources(); loadSummary(); }} /></TabsContent>
        <TabsContent value="history"><HistoryTab reloadAll={reloadAll} /></TabsContent>
      </Tabs>
      {newTopic && <NewTopic onClose={() => setNewTopic(false)} />}
      {tplOpen && <TemplatesEditor onClose={() => setTplOpen(false)} />}
    </div>
  );
}
